import React, { useEffect, useState, useCallback } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'

import { CompanyDetail as CompanyDetailResp, CompanyGeneratedQuestion, clientApi } from '@/api/client'
import ErrorBox from '@/components/ErrorBox'
import Topbar from '@/components/Topbar'
import BackLink from '@/components/BackLink'
import Freshness from '@/components/Freshness'
import { MetricTile, Posture, SkeletonPanel } from '@/components/primitives'
import ScenarioFrameCard, { normalizeScenarioFrame } from '@/components/ScenarioFrameCard'
import DownloadReportButton from '@/components/DownloadReportButton'
import RenderBoundary from '@/components/RenderBoundary'
import { fmtProb, timeAgo, freshest } from '@/utils/format'

const REPORT_TYPE_LABELS: Record<string, string> = {
  research: 'Research',
  forecast_review: 'Forecast Review',
  monitor_update: 'Monitor Update',
}

function summarizeText(value: any, fallback?: string | null): string | null {
  if (typeof value === 'string' && value.trim()) return value.trim()
  if (Array.isArray(value)) {
    const joined = value
      .map((item) => {
        if (typeof item === 'string') return item.trim()
        if (!item || typeof item !== 'object') return ''
        return String(item.title || item.name || item.label || item.summary || item.text || item.description || '').trim()
      })
      .filter(Boolean)
      .slice(0, 4)
      .join(' ')
    if (joined) return joined
  }
  if (value && typeof value === 'object') {
    const candidate = value.summary || value.text || value.description || value.title || value.label || value.name
    if (typeof candidate === 'string' && candidate.trim()) return candidate.trim()
  }
  if (fallback && fallback.trim()) return fallback.trim()
  return null
}

function asItems(value: any): any[] {
  if (!value) return []
  if (Array.isArray(value)) return value.filter(Boolean)
  if (typeof value === 'string') return value.split(/\n+/).map((item) => item.trim()).filter(Boolean)
  if (typeof value === 'object') return [value]
  return []
}

function formatListItem(item: any): string {
  if (!item) return ''
  if (typeof item === 'string') return item
  if (typeof item === 'number') return String(item)
  return String(
    item.title ||
    item.name ||
    item.label ||
    item.question ||
    item.summary ||
    item.text ||
    item.description ||
    item.query ||
    '',
  ).trim()
}

function questionStatusPillClass(status: string | null | undefined): string {
  const normalized = String(status || '').toLowerCase()
  if (normalized === 'approved' || normalized === 'active') return 'pill ok'
  if (normalized === 'rejected') return 'pill bad'
  if (normalized === 'retired') return 'pill'
  return 'pill'
}

function riskPillClass(risk: string | null | undefined): string {
  const normalized = String(risk || '').toLowerCase()
  if (normalized === 'high') return 'pill bad'
  if (normalized === 'medium') return 'pill'
  if (normalized === 'low') return 'pill ok'
  return 'pill'
}

function scoreLabel(score: any): string | null {
  if (typeof score !== 'number' || !Number.isFinite(score)) return null
  return `${Math.round(score * 100)}%`
}

function keyQuestionScore(question: CompanyGeneratedQuestion): number {
  const biz = typeof question.business_value_score === 'number' ? question.business_value_score : 0.5
  const res = typeof question.resolvability_score === 'number' ? question.resolvability_score : 0.5
  const src = typeof question.source_quality_score === 'number' ? question.source_quality_score : 0.5
  let score = (biz * 0.5) + (res * 0.3) + (src * 0.2)
  const risk = String(question.template_risk || '').toLowerCase()
  if (risk === 'high') score -= 0.12
  else if (risk === 'medium') score -= 0.05
  if (question.forecast && typeof question.forecast.probability === 'number') score += 0.03
  return Math.max(0, Math.min(1, score))
}

function hasForecast(question: CompanyGeneratedQuestion): boolean {
  return !!(question.forecast && typeof question.forecast.probability === 'number')
}

function normalizeMemoText(value?: string | null): string {
  return String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function distinctMemoText(value?: string | null, ...existing: Array<string | null | undefined>): string | null {
  const text = summarizeText(value)
  if (!text) return null
  const norm = normalizeMemoText(text)
  if (!norm) return null
  for (const other of existing) {
    const otherNorm = normalizeMemoText(other)
    if (!otherNorm) continue
    if (norm === otherNorm) return null
    if (norm.length > 80 && otherNorm.includes(norm)) return null
    if (otherNorm.length > 80 && norm.includes(otherNorm)) return null
  }
  return text
}

function isGenericMemoStance(value?: string | null): boolean {
  const text = String(value || '').trim().toLowerCase()
  return text === 'hold' || text === 'wait' || text === 'monitor' || text === 'monitor and wait'
}

// `MemoMetric` is kept as a thin alias to the shared {@link MetricTile}
// primitive so existing call sites in this file continue to work. New
// consumers should import `MetricTile` directly from
// `@/components/primitives`.
function MemoMetric({
  label,
  value,
  tone,
}: {
  label: string
  value: string | number
  tone?: 'ok' | 'warn' | 'bad'
}) {
  return <MetricTile label={label} value={value} tone={tone ?? 'accent'} />
}

function MemoTextPanel({ title, text, accent }: { title: string; text?: string | null; accent?: string }) {
  if (!text) return null
  return (
    <div style={{ padding: 16, borderRadius: 16, border: '1px solid var(--border)', background: 'var(--card)' }}>
      <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5, color: accent || 'var(--accent, #6366f1)', marginBottom: 8 }}>{title}</div>
      <div style={{ fontSize: 14, lineHeight: 1.55 }}>{text}</div>
    </div>
  )
}

function ForecastImpactCard({ question }: { question: CompanyGeneratedQuestion }) {
  const p = question.forecast?.probability
  const evidence = scoreLabel(question.source_quality_score)
  const business = scoreLabel(question.business_value_score)
  const risk = String(question.template_risk || '').toLowerCase()
  const description = question.description ? String(question.description).trim() : ''
  return (
    <div style={{ padding: 14, borderRadius: 16, border: '1px solid var(--border)', background: 'linear-gradient(180deg, rgba(99,102,241,0.08), rgba(99,102,241,0.02))' }}>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 13, lineHeight: 1.4 }}>{question.title}</div>
          {description && <div className="muted" style={{ marginTop: 4, fontSize: 12, lineHeight: 1.45 }}>{description}</div>}
        </div>
        <div style={{ fontSize: 22, fontWeight: 850, color: 'var(--accent, #6366f1)', whiteSpace: 'nowrap' }}>{typeof p === 'number' ? fmtProb(p) : '—'}</div>
      </div>
      <div className="row" style={{ gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
        {question.category && <span className="pill">{question.category}</span>}
        {question.close_ts && <span className="pill">Close {String(question.close_ts).slice(0, 10)}</span>}
        {evidence && <span className="pill">evidence {evidence}</span>}
        {business && <span className="pill ok">impact {business}</span>}
        {risk && <span className={riskPillClass(risk)}>risk {risk}</span>}
      </div>
    </div>
  )
}

function MemoForecastCard({ row }: { row: any }) {
  return (
    <div style={{ padding: 14, borderRadius: 16, border: '1px solid var(--border)', background: 'linear-gradient(180deg, rgba(99,102,241,0.08), rgba(99,102,241,0.02))' }}>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 750, fontSize: 13, lineHeight: 1.4 }}>{row.title || row.question || 'Forecast'}</div>
          {row.why_it_matters && <div className="muted" style={{ marginTop: 6, fontSize: 12, lineHeight: 1.5 }}>{row.why_it_matters}</div>}
          {row.what_would_move_it && <div style={{ marginTop: 8, fontSize: 12, lineHeight: 1.5 }}><strong>Moves if:</strong> {row.what_would_move_it}</div>}
          {row.consequence && <div style={{ marginTop: 6, fontSize: 12, lineHeight: 1.5 }}><strong>Consequence:</strong> {row.consequence}</div>}
        </div>
        <div style={{ fontSize: 22, fontWeight: 850, color: 'var(--accent, #6366f1)', whiteSpace: 'nowrap' }}>{typeof row.probability === 'number' ? fmtProb(row.probability) : '—'}</div>
      </div>
      <div className="row" style={{ gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
        {row.target_date && <span className="pill">By {String(row.target_date).slice(0, 10)}</span>}
        {row.magnitude && <span className="pill ok">{row.magnitude} impact</span>}
        {row.direction && <span className="pill">{row.direction}</span>}
        {typeof row.decision_weight === 'number' && <span className="pill">weight {Math.round(row.decision_weight * 100)}</span>}
      </div>
    </div>
  )
}

// MemoScenarioCard is now a thin wrapper over the shared
// {@link ScenarioFrameCard}. The local definition is kept so existing call
// sites in this file (and in the Details-mode scenarios section) hit the
// same render path.
function MemoScenarioCard({ scenario }: { scenario: any }) {
  const frame = normalizeScenarioFrame(scenario)
  if (!frame) return null
  return <ScenarioFrameCard frame={frame} />
}

function MiniList({ title, items }: { title: string; items: any[] }) {
  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--muted)' }}>{title}</div>
      <ul style={{ margin: '4px 0 0 18px', padding: 0, fontSize: 12, lineHeight: 1.45 }}>
        {items.slice(0, 4).map((item, idx) => <li key={idx}>{formatListItem(item)}</li>)}
      </ul>
    </div>
  )
}

function CompanyMemoView({
  currentView,
  whatChanged,
  whyItMatters,
  watchpoints,
  questions,
  approvedCount,
  forecastedApprovedCount,
  signals,
  forecastReview,
  monitorUpdate,
  memoArtifact,
  memoGeneratedAt,
  lastMeaningfulUpdateAt,
  scenarioCardsPublicReady,
  scenarioReadinessNote,
}: {
  currentView?: string | null
  whatChanged?: string | null
  whyItMatters?: string | null
  watchpoints: any[]
  questions: CompanyGeneratedQuestion[]
  approvedCount: number
  forecastedApprovedCount: number
  signals: any[]
  forecastReview?: any
  monitorUpdate?: any
  memoArtifact?: any
  memoGeneratedAt?: string | null
  lastMeaningfulUpdateAt?: string | null
  scenarioCardsPublicReady?: boolean
  scenarioReadinessNote?: string | null
}) {
  const artifact = memoArtifact?.artifact && typeof memoArtifact.artifact === 'object' ? memoArtifact.artifact : memoArtifact
  const call = artifact?.call && typeof artifact.call === 'object' ? artifact.call : null
  const posture = artifact?.forecast_posture && typeof artifact.forecast_posture === 'object' ? artifact.forecast_posture : null
  const thesis = artifact?.thesis && typeof artifact.thesis === 'object' ? artifact.thesis : null
  const variant = artifact?.variant_view && typeof artifact.variant_view === 'object' ? artifact.variant_view : null
  const variantHasMarketBaseline = variant?.has_market_baseline === true || variant?.probability_basis === 'market_implied'
  const scenarios = Array.isArray(artifact?.scenario_frame) ? artifact.scenario_frame : []
  const forecastStack = Array.isArray(artifact?.forecast_stack) ? artifact.forecast_stack : []
  const breakers = asItems(artifact?.thesis_breakers)
  const artifactWatchpoints = asItems(artifact?.watchpoints)
  const topQuestions = questions.filter(hasForecast).slice(0, 4)
  const primaryThesis = thesis?.summary || currentView || monitorUpdate?.executive_summary || forecastReview?.executive_summary
  const latestSummary = posture?.assessment || call?.summary || primaryThesis
  const currentPanelText = primaryThesis
  const changedPanelText = distinctMemoText(whatChanged, latestSummary, currentPanelText)
  const relevancePanelText = distinctMemoText(whyItMatters, latestSummary, currentPanelText, changedPanelText)
  return (
    <div style={{ marginTop: 16 }}>
      <div className="company-detail-posture">
        <Posture
          headline={posture?.headline || call?.headline || 'What this means now'}
          label={
            posture?.label
              || (call?.stance && !isGenericMemoStance(call.stance) ? call.stance : null)
          }
          summary={
            latestSummary
            || 'No synthesized memo is available yet. Refresh this company to generate the latest PM-facing view.'
          }
          mostLikelyCase={posture?.most_likely_case || null}
          nextProofPoint={posture?.next_proof_point || null}
          dominantRisk={posture?.dominant_risk || null}
          rightSlot={
            (memoGeneratedAt || lastMeaningfulUpdateAt)
              ? (
                <Freshness
                  label="Memo"
                  iso={freshest(memoGeneratedAt, lastMeaningfulUpdateAt)}
                  staleAfterMs={24 * 3600 * 1000}
                />
              )
              : null
          }
        />
        <div className="grid" style={{ marginTop: 16, gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))' }}>
          <MetricTile label="Key questions" value={approvedCount} tone="ok" />
          <MetricTile
            label="Current forecasts"
            value={`${forecastedApprovedCount}/${Math.max(approvedCount, 1)}`}
          />
          <MetricTile
            label="Watchpoints"
            value={watchpoints.length}
            tone={watchpoints.length ? 'warn' : 'neutral'}
          />
          <MetricTile label="Signals" value={signals.length} />
        </div>
      </div>

      {variant && (asItems(variant.priced_in).length > 0 || asItems(variant.variant_view).length > 0) && (
        <div className="twoCol" style={{ marginTop: 16 }}>
          <div style={{ padding: 16, borderRadius: 16, border: '1px solid var(--border)', background: 'var(--card)' }}>
            <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--muted)', marginBottom: 8 }}>{variantHasMarketBaseline ? 'Market / Narrative Setup' : 'Forecast / Narrative Setup'}</div>
            <MiniList title={variantHasMarketBaseline ? 'Appears priced in' : 'Higher-probability Cenva setup'} items={asItems(variant.priced_in)} />
            {!variantHasMarketBaseline && (
              <div className="muted" style={{ fontSize: 12, marginTop: 8 }}>
                These are Cenva internal forecast reads, not market-implied odds.
              </div>
            )}
          </div>
          <div style={{ padding: 16, borderRadius: 16, border: '1px solid var(--border)', background: 'var(--card)' }}>
            <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--accent, #6366f1)', marginBottom: 8 }}>{variantHasMarketBaseline ? 'Cenva Variant View' : 'Lower-Probability Cenva View'}</div>
            <MiniList title={variantHasMarketBaseline ? 'Where we differ' : 'Where Cenva is more skeptical'} items={asItems(variant.variant_view)} />
          </div>
        </div>
      )}

      {scenarios.length > 0 && scenarioCardsPublicReady && (
        <div className="card" style={{ marginTop: 16 }}>
          <h2 style={{ margin: '0 0 4px' }}>Scenario Frame</h2>
          <div className="muted" style={{ fontSize: 12, marginBottom: 12 }}>Forward cases with PM-facing labels, not raw bull/base/bear jargon.</div>
          <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
            {scenarios.map((s: any, idx: number) => <MemoScenarioCard key={`${s.case || 'scenario'}-${idx}`} scenario={s} />)}
          </div>
        </div>
      )}

      {scenarios.length > 0 && !scenarioCardsPublicReady && (
        <div className="card" style={{ marginTop: 16, borderLeft: '3px solid var(--warn, #f59e0b)' }}>
          <h2 style={{ margin: '0 0 4px' }}>Scenario outlook pending</h2>
          <div className="muted" style={{ fontSize: 13, lineHeight: 1.6 }}>
            {scenarioReadinessNote || 'Current forecasts are available below; portfolio-level scenario synthesis is still pending.'}
          </div>
        </div>
      )}

      {(currentPanelText || changedPanelText) && (
        <div className="twoCol" style={{ marginTop: 16 }}>
          <MemoTextPanel title="Current Thesis" text={currentPanelText} />
          <MemoTextPanel title="What Changed" text={changedPanelText} accent="var(--warn, #f59e0b)" />
        </div>
      )}

      <div className="twoCol" style={{ marginTop: 16 }}>
        <MemoTextPanel title="Decision Relevance" text={relevancePanelText} accent="var(--ok, #16a34a)" />
        <div style={{ padding: 16, borderRadius: 16, border: '1px solid var(--border)', background: 'var(--card)' }}>
          <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--accent, #6366f1)', marginBottom: 8 }}>Next Watchpoints</div>
          {watchpoints.length > 0 ? <WatchpointList items={watchpoints.slice(0, 5)} /> : <div className="muted" style={{ fontSize: 13 }}>No watchpoints available yet.</div>}
        </div>
      </div>

      <div className="card" style={{ marginTop: 16 }}>
        <div className="row" style={{ justifyContent: 'space-between', gap: 12, alignItems: 'center', marginBottom: 12 }}>
          <div>
            <h2 style={{ margin: 0 }}>Forecast Impact</h2>
            <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>Highest-priority live forecasts translated into decision signals.</div>
          </div>
          {forecastReview?.generated_at && <span className="pill">Review {timeAgo(forecastReview.generated_at)}</span>}
        </div>
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
          {forecastStack.length > 0
            ? forecastStack.map((row: any, idx: number) => <MemoForecastCard key={`${row.question_id || 'forecast'}-${idx}`} row={row} />)
            : topQuestions.map((q) => <ForecastImpactCard key={q.id} question={q} />)}
          {forecastStack.length === 0 && topQuestions.length === 0 && <div className="muted" style={{ fontSize: 13 }}>No current forecasted questions yet.</div>}
        </div>
      </div>

      {(breakers.length > 0 || artifactWatchpoints.length > 0) && (
        <div className="twoCol" style={{ marginTop: 16 }}>
          <div style={{ padding: 16, borderRadius: 16, border: '1px solid var(--border)', background: 'var(--card)' }}>
            <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--bad, #dc2626)', marginBottom: 8 }}>Thesis Breakers</div>
            {breakers.length > 0 ? <MiniList title="Fails if" items={breakers.map((b: any) => b.condition || b.breaker || b)} /> : <div className="muted" style={{ fontSize: 13 }}>No thesis breakers available yet.</div>}
          </div>
          <div style={{ padding: 16, borderRadius: 16, border: '1px solid var(--border)', background: 'var(--card)' }}>
            <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--accent, #6366f1)', marginBottom: 8 }}>Memo Watchpoints</div>
            {artifactWatchpoints.length > 0 ? <MiniList title="Watch next" items={artifactWatchpoints.map((w: any) => w.watchpoint || w.title || w)} /> : <div className="muted" style={{ fontSize: 13 }}>No memo watchpoints available yet.</div>}
          </div>
        </div>
      )}
    </div>
  )
}

function formatDueLabel(daysUntil: number | null | undefined, resolvedAt: string | null | undefined, status: string): string | null {
  if (status && status !== 'pending') return null
  if (daysUntil === null || daysUntil === undefined || Number.isNaN(daysUntil)) return null
  const d = Math.trunc(daysUntil)
  if (d < 0) return `${Math.abs(d)}d overdue`
  if (d === 0) return 'due today'
  if (d === 1) return 'due tomorrow'
  if (d <= 30) return `due in ${d}d`
  return null
}

function watchpointStatusPill(status: string, isOverdue: boolean): { label: string; className: string } | null {
  const s = String(status || '').toLowerCase()
  if (s === 'resolved_yes') return { label: 'resolved · yes', className: 'pill ok' }
  if (s === 'resolved_no') return { label: 'resolved · no', className: 'pill warn' }
  if (s === 'delayed') return { label: 'delayed', className: 'pill warn' }
  if (s === 'superseded') return { label: 'superseded', className: 'pill' }
  if (s === 'unknown') return { label: 'unverified', className: 'pill' }
  if (isOverdue) return { label: 'overdue', className: 'pill warn' }
  return null
}

function WatchpointList({ items }: { items: any[] }) {
  return (
    <ul style={{ listStyle: 'none', margin: 0, padding: 0, fontSize: 12, lineHeight: 1.55 }}>
      {items.map((wp, idx) => {
        const item = (wp && typeof wp === 'object' && !Array.isArray(wp)) ? wp : { title: formatListItem(wp) }
        const title = String(item.title || '').trim()
        if (!title) return null
        const status = String(item.status || 'pending').toLowerCase()
        const isOverdue = !!item.is_overdue
        const dueLabel = formatDueLabel(item.days_until_due, item.resolved_at, status)
        const pill = watchpointStatusPill(status, isOverdue)
        const eventType = item.event_type ? String(item.event_type).replace(/_/g, ' ') : null
        const detail = item.detail ? String(item.detail) : null
        const resolutionOutcome = item.resolution_outcome ? String(item.resolution_outcome) : null
        const topics: string[] = Array.isArray(item.impacted_forecast_topics) ? item.impacted_forecast_topics.filter(Boolean).slice(0, 4) : []
        return (
          <li key={idx} style={{ marginBottom: 10, paddingBottom: 8, borderBottom: idx < items.length - 1 ? '1px solid var(--border)' : 'none' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 500, flex: '1 1 auto' }}>{title}</span>
              {pill && <span className={pill.className} style={{ fontSize: 10 }}>{pill.label}</span>}
              {dueLabel && !pill && <span className="pill" style={{ fontSize: 10 }}>{dueLabel}</span>}
              {dueLabel && pill && <span className="muted" style={{ fontSize: 11 }}>{dueLabel}</span>}
            </div>
            {eventType && (
              <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>{eventType}</div>
            )}
            {detail && <div style={{ marginTop: 4, color: 'var(--inkMuted)' }}>{detail}</div>}
            {resolutionOutcome && (
              <div style={{ marginTop: 4, fontSize: 12 }}>
                <span className="muted" style={{ marginRight: 6 }}>outcome:</span>
                {resolutionOutcome}
              </div>
            )}
            {topics.length > 0 && (
              <div style={{ marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {topics.map((t, i) => (
                  <span key={i} className="pill" style={{ fontSize: 10 }}>{t}</span>
                ))}
              </div>
            )}
          </li>
        )
      })}
    </ul>
  )
}

function InfoHint({ text }: { text: string }) {
  const [open, setOpen] = useState(false)
  return (
    <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
      <button
        type="button"
        className="btn"
        onClick={(e) => {
          e.stopPropagation()
          setOpen((v) => !v)
        }}
        style={{
          padding: '2px 7px',
          minHeight: 0,
          borderRadius: 999,
          fontSize: 11,
          lineHeight: 1.2,
        }}
        aria-label="More information"
      >
        i
      </button>
      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            right: 0,
            zIndex: 20,
            width: 260,
            padding: '10px 12px',
            borderRadius: 12,
            border: '1px solid var(--border)',
            background: 'var(--panelSolid)',
            boxShadow: 'var(--shadow)',
            fontSize: 12,
            lineHeight: 1.5,
            color: 'var(--ink)',
          }}
        >
          {text}
        </div>
      )}
    </span>
  )
}

function CollapsibleCard({
  title,
  hint,
  summary,
  children,
  defaultOpen = false,
}: {
  title: string
  hint?: string
  summary?: string | null
  children: React.ReactNode
  defaultOpen?: boolean
}) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
        <div className="row" style={{ gap: 8, alignItems: 'center' }}>
          <h2 style={{ margin: 0 }}>{title}</h2>
          {hint && <InfoHint text={hint} />}
        </div>
        <button className="btn" type="button" onClick={() => setOpen((v) => !v)}>
          {open ? 'Hide' : 'Show'}
        </button>
      </div>
      {!open && summary && (
        <div className="muted" style={{ fontSize: 12, marginTop: 8, lineHeight: 1.5 }}>
          {summary}
        </div>
      )}
      {open && <div style={{ marginTop: 12 }}>{children}</div>}
    </div>
  )
}

/* ---------- Agent card (inline sub-component) ---------- */

/** Render a string or array-of-strings as bullet list, or a primitive as text. */
function renderTextOrList(val: any, label?: string) {
  if (!val) return null
  if (Array.isArray(val)) {
    const items = val.filter(Boolean)
    if (!items.length) return null
    return (
      <div style={{ marginTop: 6 }}>
        {label && <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', marginBottom: 2 }}>{label}</div>}
        <ul style={{ margin: '2px 0 0 16px', padding: 0, fontSize: 12, lineHeight: 1.5 }}>
          {items.slice(0, 12).map((item: any, i: number) => (
            <li key={i} style={{ marginBottom: 3 }}>
              {typeof item === 'string' ? item : renderStructuredItem(item)}
            </li>
          ))}
        </ul>
      </div>
    )
  }
  return (
    <div style={{ fontSize: 12, lineHeight: 1.5, marginTop: 4 }}>
      {label && <span style={{ fontWeight: 700 }}>{label}: </span>}
      {String(val)}
    </div>
  )
}

/** Render a {driver/uncertainty/question, description} object as a labeled line. */
function renderStructuredItem(item: any) {
  if (typeof item === 'string') return item
  if (typeof item !== 'object' || !item) return JSON.stringify(item)
  // Pick the "name" from whichever key: driver, uncertainty, question, sub_question, title
  const name = item.driver || item.uncertainty || item.factor || item.question || item.sub_question || item.title || item.name
  const desc = item.description || item.summary || item.snippet
  if (name && desc) {
    return <><span style={{ fontWeight: 600 }}>{name}</span> &mdash; <span className="muted">{desc}</span></>
  }
  if (name) return <span style={{ fontWeight: 600 }}>{name}</span>
  if (desc) return <>{desc}</>
  return JSON.stringify(item)
}

function AgentCard({ agent }: { agent: any }) {
  const [open, setOpen] = useState(false)
  const output = agent.output || {}
  const hasForecastProb = typeof agent.prob === 'number'

  return (
    <div className={`agent-card${hasForecastProb ? '' : ' agent-card--utility'}`}>
      <div className="agent-card-header" onClick={() => setOpen(o => !o)} style={{ cursor: 'pointer' }}>
        <div style={{ flex: 1 }}>
          <span style={{ fontWeight: 700, fontSize: 13 }}>{agent.name}</span>
          {hasForecastProb && (
            <span style={{ marginLeft: 10, fontFamily: 'var(--mono)', fontWeight: 700, color: 'var(--accent)' }}>
              {fmtProb(agent.prob)}
            </span>
          )}
          {typeof agent.confidence === 'number' && (
            <span className="muted" style={{ marginLeft: 8, fontSize: 11 }}>
              conf: {(agent.confidence * 100).toFixed(0)}%
            </span>
          )}
        </div>
        <span className="muted" style={{ fontSize: 11, flexShrink: 0 }}>{open ? '\u25B2' : '\u25BC'}</span>
      </div>
      {agent.summary && !open && (
        <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>{String(agent.summary).slice(0, 160)}</div>
      )}
      {open && (
        <div className="agent-card-detail">
          {/* Reasoning (string) */}
          {typeof output.reasoning === 'string' && output.reasoning && (
            <div style={{ fontSize: 12, lineHeight: 1.6, marginTop: 6, whiteSpace: 'pre-wrap' }}>{output.reasoning}</div>
          )}
          {!output.reasoning && agent.summary && (
            <div style={{ fontSize: 12, lineHeight: 1.6, marginTop: 6 }}>{agent.summary}</div>
          )}

          {/* Structured: key_points */}
          {renderTextOrList(output.key_points, 'Key Points')}

          {/* Structured: drivers */}
          {renderTextOrList(output.drivers, 'Drivers')}

          {/* Structured: uncertainties */}
          {renderTextOrList(output.uncertainties, 'Uncertainties')}

          {/* Structured: key_uncertainties (premortem agent) */}
          {renderTextOrList(output.key_uncertainties, 'Key Uncertainties')}

          {/* Premortem: confidence killer */}
          {output.confidence_killer && (
            <div style={{ marginTop: 6, fontSize: 12 }}>
              <span style={{ fontWeight: 700, color: 'var(--warn)' }}>Confidence killer: </span>{output.confidence_killer}
            </div>
          )}

          {/* Fermi: sub-questions */}
          {renderTextOrList(output.sub_questions, 'Sub-Questions')}

          {/* Web search: final results */}
          {output.final_results && Array.isArray(output.final_results) && output.final_results.length > 0 && (
            <div style={{ marginTop: 6 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', marginBottom: 2 }}>Search Results</div>
              <ul style={{ margin: '2px 0 0 16px', padding: 0, fontSize: 12 }}>
                {output.final_results.slice(0, 8).map((r: any, i: number) => (
                  <li key={i} style={{ marginBottom: 3 }}>
                    <span style={{ fontWeight: 600 }}>{r.title || r.url}</span>
                    {r.snippet && <span className="muted"> &mdash; {String(r.snippet).slice(0, 120)}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Sources */}
          {output.sources && Array.isArray(output.sources) && output.sources.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)' }}>Sources</div>
              <ul style={{ margin: '4px 0 0 16px', padding: 0, fontSize: 12 }}>
                {output.sources.slice(0, 8).map((src: any, i: number) => (
                  <li key={i} style={{ marginBottom: 2 }}>
                    {typeof src === 'string' ? src : src?.title || src?.url || JSON.stringify(src)}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Critic: issues_found */}
          {renderTextOrList(output.issues_found, 'Issues Found')}

          {/* Critic: logical_gaps */}
          {renderTextOrList(output.logical_gaps, 'Logical Gaps')}

          {/* Critic: suggested_adjustment */}
          {typeof output.suggested_adjustment === 'number' && (
            <div style={{ marginTop: 4, fontSize: 12 }}>
              <span style={{ fontWeight: 700 }}>Suggested adj: </span>
              <span style={{ fontFamily: 'var(--mono)' }}>
                {output.suggested_adjustment > 0 ? '+' : ''}{(output.suggested_adjustment * 100).toFixed(1)}%
              </span>
            </div>
          )}

          {/* Critic: overconfidence_risk */}
          {typeof output.overconfidence_risk === 'number' && (
            <div style={{ marginTop: 4, fontSize: 12 }}>
              <span style={{ fontWeight: 700 }}>Overconfidence risk: </span>
              <span style={{ fontFamily: 'var(--mono)' }}>{(output.overconfidence_risk * 100).toFixed(0)}%</span>
            </div>
          )}

          {/* Dissent score */}
          {typeof output.dissent_score === 'number' && (
            <div style={{ marginTop: 4, fontSize: 12 }}>
              <span style={{ fontWeight: 700 }}>Dissent: </span>
              <span style={{ fontFamily: 'var(--mono)' }}>{output.dissent_score.toFixed(2)}</span>
            </div>
          )}

          {/* LLM summary (pre_research agent) */}
          {output.llm_summary && (
            <div style={{ fontSize: 12, lineHeight: 1.5, marginTop: 6 }}>{output.llm_summary}</div>
          )}
        </div>
      )}
    </div>
  )
}

/* ---------- Forecast reasoning panel ---------- */

function ForecastReasoningPanel({ detail }: { detail: any }) {
  if (!detail) return null

  const agents: any[] = detail.agents || []
  const forecastAgents = agents.filter((a: any) => typeof a.prob === 'number')
  const utilityAgents = agents.filter((a: any) => typeof a.prob !== 'number')
  const meta = detail.notes_parsed || {}
  const ci = meta.confidence_interval
  const review = meta.high_edge_review
  const scenario = detail.scenario && typeof detail.scenario === 'object' ? detail.scenario : null

  return (
    <div className="forecast-detail-panel">
      {/* Summary pills */}
      <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        {typeof detail.our_prob === 'number' && (
          <span className="pill ok">Forecast: {fmtProb(detail.our_prob)}</span>
        )}
        {typeof detail.market_prob === 'number' && (
          <span className="pill">Market: {fmtProb(detail.market_prob)}</span>
        )}
        {typeof detail.edge === 'number' && (
          <span className={`pill ${detail.edge > 0 ? 'ok' : detail.edge < -0.05 ? 'bad' : ''}`}>
            Edge: {detail.edge > 0 ? '+' : ''}{(detail.edge * 100).toFixed(1)}%
          </span>
        )}
        {ci && (
          <span className="pill">
            CI: {typeof ci.low === 'number' ? fmtProb(ci.low) : '?'}–{typeof ci.high === 'number' ? fmtProb(ci.high) : '?'}
          </span>
        )}
        {typeof meta.spread === 'number' && (
          <span className="pill">Spread: {(meta.spread * 100).toFixed(1)}%</span>
        )}
        {forecastAgents.length > 0 && (
          <span className="pill">{forecastAgents.length} agent{forecastAgents.length !== 1 ? 's' : ''}</span>
        )}
      </div>

      {/* High-edge review note */}
      {review && typeof review === 'object' && review.adjustment_reason && (
        <div style={{ fontSize: 12, marginBottom: 10, padding: '8px 10px', borderRadius: 8, background: 'rgba(251, 191, 36, 0.08)', border: '1px solid rgba(251, 191, 36, 0.2)' }}>
          <span style={{ fontWeight: 700, color: 'var(--warn)' }}>Review: </span>
          {review.adjustment_reason}
        </div>
      )}

      {scenario && Array.isArray(scenario.assumptions) && scenario.assumptions.length > 0 && (
        <div style={{ fontSize: 12, marginBottom: 10, padding: '8px 10px', borderRadius: 8, background: 'rgba(59, 130, 246, 0.08)', border: '1px solid rgba(59, 130, 246, 0.18)' }}>
          <div style={{ fontWeight: 700, color: 'var(--accent)', marginBottom: 4 }}>
            Conditional Scenario{scenario.label ? `: ${scenario.label}` : ''}
          </div>
          <div className="muted" style={{ marginBottom: 4 }}>
            This forecast is conditioned on the following assumptions being true.
          </div>
          <ul style={{ margin: '0 0 0 16px', padding: 0, lineHeight: 1.5 }}>
            {scenario.assumptions.slice(0, 8).map((a: any, idx: number) => (
              <li key={a?.id || idx}>{String(a?.text || '').trim()}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Forecast agents */}
      {forecastAgents.length > 0 && (
        <div style={{ marginBottom: utilityAgents.length > 0 ? 12 : 0 }}>
          <div className="muted" style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', marginBottom: 6 }}>
            Forecasting Agents
          </div>
          <div className="stack" style={{ gap: 6 }}>
            {forecastAgents.map((a: any, i: number) => <AgentCard key={a.name || i} agent={a} />)}
          </div>
        </div>
      )}

      {/* Utility agents */}
      {utilityAgents.length > 0 && (
        <div>
          <div className="muted" style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', marginBottom: 6 }}>
            Analysis Agents
          </div>
          <div className="stack" style={{ gap: 6 }}>
            {utilityAgents.map((a: any, i: number) => <AgentCard key={a.name || i} agent={a} />)}
          </div>
        </div>
      )}

      {detail.started_at && (
        <div className="muted" style={{ fontSize: 11, marginTop: 10 }}>
          Run #{detail.run_id} &middot; {timeAgo(detail.started_at)}
        </div>
      )}
    </div>
  )
}

export default function CompanyDetail() {
  const { companyId } = useParams()
  const nav = useNavigate()
  const [data, setData] = useState<CompanyDetailResp | null>(null)
  const [error, setError] = useState<any>(null)
  const [refreshing, setRefreshing] = useState(false)
  const [expandedRunId, setExpandedRunId] = useState<number | null>(null)
  const [runDetail, setRunDetail] = useState<any>(null)
  const [runDetailLoading, setRunDetailLoading] = useState(false)
  const [questionActionId, setQuestionActionId] = useState<number | null>(null)
  const [questionActionError, setQuestionActionError] = useState<string | null>(null)
  const [showAllQuestions, setShowAllQuestions] = useState(false)
  const [viewMode, setViewMode] = useState<'memo' | 'details'>('memo')
  const companyIdNum = Number(companyId)
  const companyIdValid = Number.isFinite(companyIdNum) && companyIdNum > 0

  const loadDetail = useCallback(async () => {
    if (!companyIdValid) return
    const detail = await clientApi.companyDetail(companyIdNum)
    setData(detail)
  }, [companyIdNum, companyIdValid])

  useEffect(() => {
    let cancelled = false
    if (!companyIdValid) {
      setError(new Error('Invalid company id'))
      return
    }
    loadDetail().catch((ex) => {
      if (!cancelled) setError(ex)
    })
    return () => {
      cancelled = true
    }
  }, [companyIdValid, loadDetail])

  async function handleRefresh() {
    if (!companyIdValid) return
    setRefreshing(true)
    try {
      await clientApi.refreshCompany(companyIdNum)
      await loadDetail()
    } catch (ex) {
      setError(ex)
    } finally {
      setRefreshing(false)
    }
  }

  async function handleUntrack() {
    if (!companyIdValid) return
    try {
      await clientApi.untrackCompany(companyIdNum)
      nav('/universe/companies')
    } catch (ex) {
      setError(ex)
    }
  }

  async function handleTrack() {
    if (!companyIdValid) return
    setRefreshing(true)
    try {
      await clientApi.trackCompany(undefined, companyIdNum)
      await loadDetail()
    } catch (ex) {
      setError(ex)
    } finally {
      setRefreshing(false)
    }
  }

  async function handleQuestionModeration(questionId: number, status: 'approved' | 'rejected' | 'retired' | 'proposed') {
    if (!companyIdValid || !data?.viewer?.can_moderate_questions) return
    setQuestionActionId(questionId)
    setQuestionActionError(null)
    try {
      await clientApi.updateCompanyQuestion(companyIdNum, questionId, { status })
      await loadDetail()
    } catch (ex: any) {
      setQuestionActionError(ex?.message || 'Failed to update question status')
    } finally {
      setQuestionActionId(null)
    }
  }

  if (!data && !error) return (
    <div className="container">
      <Topbar subtitle="Company" />
      <BackLink defaultTo="/universe/companies" defaultLabel="Companies" />
      <SkeletonPanel kind="report" />
    </div>
  )

  const company = data?.company || {}
  const report = data?.report
  const baselineReport = data?.baseline_report
  const monitorUpdate = data?.monitor_update
  const forecastReview = data?.forecast_review
  const memoArtifact = data?.memo_artifact
  const memoGeneratedAt = (data?.track as any)?.memo_generated_at
    || (memoArtifact as any)?.generated_at
    || (data?.card_summary as any)?.memo_generated_at
    || null
  const lastMeaningfulUpdateAt = (data?.track as any)?.last_meaningful_update_at
    || (data?.card_summary as any)?.last_meaningful_update_at
    || (data?.company_health as any)?.last_successful_research_at
    || null
  const reportHistory: any[] = data?.report_history || []
  const signals: any[] = data?.signals || []
  const hiringSignals: any[] = data?.hiring_signals || []
  const hiringRollup: any = data?.hiring_rollup || null
  const questions: CompanyGeneratedQuestion[] = data?.questions || []
  const approvedQuestions: CompanyGeneratedQuestion[] = data?.approved_questions?.length ? data.approved_questions : questions
  const proposedQuestions: CompanyGeneratedQuestion[] = data?.proposed_questions || []
  const inactiveQuestions: CompanyGeneratedQuestion[] = data?.inactive_questions || []
  const monitors: any[] = data?.monitors || []
  const artifact = data?.artifact || {}
  const deltaSummary = data?.delta_summary || {}
  const questionStatusCounts = data?.question_status_counts || {}
  const companyHealth = data?.company_health || {}
  const cardSummary = data?.card_summary || {}
  const outlookQuality = (data as any)?.outlook_quality || {}
  const scenarioCardsPublicReady = outlookQuality.scenario_cards_public_ready === true
  const scenarioReadinessNote = typeof outlookQuality.note === 'string' ? outlookQuality.note : null
  const memoArtifactBody = memoArtifact?.artifact && typeof memoArtifact.artifact === 'object' ? memoArtifact.artifact : memoArtifact
  const memoScenarioFrame = Array.isArray(memoArtifactBody?.scenario_frame) ? memoArtifactBody.scenario_frame : []
  const isStaff = !!data?.viewer?.can_moderate_questions
  // Pick the first source that actually has bull/base/bear populated. A
  // naive `report?.scenarios || baseline?.scenarios` short-circuits on an
  // empty object (truthy in JS), so rows written with `scenarios = {}`
  // (e.g. older reflection-style rows) would hide the real scenarios
  // stored on baseline or forecast_review.
  const _pickScenarios = (s: any) =>
    s && typeof s === 'object' && (s.bull || s.base || s.bear) ? s : null
  const scenarios =
    _pickScenarios(forecastReview?.scenarios) ||
    _pickScenarios(report?.scenarios) ||
    _pickScenarios(baselineReport?.scenarios) ||
    {}
  const scenarioFrameCards = memoScenarioFrame.length > 0
    ? memoScenarioFrame.map((s: any) => normalizeScenarioFrame(s)).filter(Boolean)
    : (['bull', 'base', 'bear'] as const)
      .map((key) => normalizeScenarioFrame((scenarios as any)?.[key], key))
      .filter(Boolean)
  const currentView = summarizeText(
    artifact.current_view,
    cardSummary.current_view_summary || null,
  )
  const whatChanged = summarizeText(
    deltaSummary.what_changed || deltaSummary.changed_assessment || artifact.what_changed,
    cardSummary.what_changed_summary || null,
  )
  const whyItMatters = summarizeText(
    artifact.why_it_matters,
    null,
  )
  const watchpoints = (() => {
    const artifactWatchpoints = asItems(data?.watchpoints)
    if (artifactWatchpoints.length > 0) return artifactWatchpoints
    const directArtifactWatchpoints = asItems(artifact.watchpoints)
    if (directArtifactWatchpoints.length > 0) return directArtifactWatchpoints
    return asItems(deltaSummary.next_watchpoints)
  })()
  const recentChangeItems = asItems(deltaSummary.items || deltaSummary.new_signals).slice(0, 4)
  const approvedCount =
    approvedQuestions.length ||
    questionStatusCounts.approved ||
    questionStatusCounts.active ||
    questionStatusCounts.live ||
    companyHealth.approved_question_count ||
    0
  const proposedCount =
    proposedQuestions.length ||
    questionStatusCounts.proposed ||
    companyHealth.proposed_question_count ||
    0
  const watchpointCount = watchpoints.length
  const forecastedApprovedCount = approvedQuestions.filter((q) => q.forecast && typeof q.forecast.probability === 'number').length
  const isTracked = !!data?.track && String(data?.track?.status || '').toLowerCase() === 'active'
  const rankedApprovedQuestions = [...approvedQuestions].sort((a, b) => keyQuestionScore(b) - keyQuestionScore(a))
  const defaultVisibleQuestionCount = isStaff ? rankedApprovedQuestions.length : 6
  const visibleApprovedQuestions = (isStaff || showAllQuestions)
    ? rankedApprovedQuestions
    : rankedApprovedQuestions.slice(0, defaultVisibleQuestionCount)
  const hiddenApprovedCount = Math.max(0, rankedApprovedQuestions.length - visibleApprovedQuestions.length)

  function toggleRunDetail(question: CompanyGeneratedQuestion) {
    const rid = question?.forecast?.run_id
    const hasRun = typeof rid === 'number'
    if (!hasRun) return
    if (expandedRunId === rid) {
      setExpandedRunId(null)
      setRunDetail(null)
      return
    }
    setExpandedRunId(rid)
    setRunDetail(null)
    setRunDetailLoading(true)
    clientApi.runDetail(rid)
      .then(setRunDetail)
      .catch(() => setRunDetail(null))
      .finally(() => setRunDetailLoading(false))
  }

  function renderQuestionRow(question: CompanyGeneratedQuestion, kind: 'approved' | 'proposed' | 'inactive') {
    const hasRun = kind === 'approved' && typeof question?.forecast?.run_id === 'number'
    const isExpanded = hasRun && expandedRunId === question.forecast!.run_id
    const resolvability = scoreLabel(question.resolvability_score)
    const sourceQuality = scoreLabel(question.source_quality_score)
    const businessValue = scoreLabel(question.business_value_score)
    const keyScore = scoreLabel(keyQuestionScore(question))
    const status = String(question.status || (kind === 'approved' ? 'approved' : kind)).toLowerCase()

    return (
      <div key={question.id} className="signal-row">
        <div
          className="row"
          style={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, cursor: hasRun ? 'pointer' : 'default' }}
          onClick={() => hasRun && toggleRunDetail(question)}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 13, lineHeight: 1.45, overflowWrap: 'break-word', wordBreak: 'break-word' }}>
              {question.title}
              {hasRun && (
                <span className="muted" style={{ marginLeft: 6, fontSize: 11 }}>
                  {isExpanded ? '\u25B2' : '\u25BC'}
                </span>
              )}
            </div>
            {question.description && (
              <div className="muted" style={{ fontSize: 12, marginTop: 3, lineHeight: 1.45 }}>
                {String(question.description).slice(0, 220)}
              </div>
            )}
          </div>
          {kind === 'approved' && question.forecast && typeof question.forecast.probability === 'number' ? (
            <div style={{ fontWeight: 700, fontSize: 15, whiteSpace: 'nowrap', color: 'var(--accent, #6366f1)' }}>
              {fmtProb(question.forecast.probability)}
            </div>
          ) : kind === 'approved' ? (
            <span className="pill" style={{ whiteSpace: 'nowrap' }}>
              forecast pending
            </span>
          ) : (
            <span className={questionStatusPillClass(status)} style={{ whiteSpace: 'nowrap' }}>
              {status}
            </span>
          )}
        </div>

        <div className="row" style={{ gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
          {question.category && <span className="pill">{question.category}</span>}
          {question.close_ts && <span className="pill">Close {String(question.close_ts).slice(0, 10)}</span>}
          {question.template_risk && <span className={riskPillClass(question.template_risk)}>risk {question.template_risk}</span>}
          {resolvability && <span className="pill">res {resolvability}</span>}
          {sourceQuality && <span className="pill">src {sourceQuality}</span>}
          {businessValue && <span className="pill">biz {businessValue}</span>}
          {kind === 'approved' && keyScore && <span className="pill ok">key {keyScore}</span>}
          {question.reviewed_at && <span className="pill">Reviewed {timeAgo(question.reviewed_at)}</span>}
          {kind === 'approved' && question.forecast?.forecasted_at && (
            <span className="pill ok">Forecasted {timeAgo(question.forecast.forecasted_at)}</span>
          )}
        </div>

        {kind !== 'approved' && isStaff && (
          <div className="row" style={{ gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
            {status !== 'approved' && (
              <button
                className="btn primary"
                onClick={(e) => {
                  e.stopPropagation()
                  handleQuestionModeration(question.id, 'approved')
                }}
                disabled={questionActionId === question.id}
              >
                {questionActionId === question.id ? 'Updating...' : 'Approve'}
              </button>
            )}
            {status !== 'rejected' && (
              <button
                className="btn"
                onClick={(e) => {
                  e.stopPropagation()
                  handleQuestionModeration(question.id, 'rejected')
                }}
                disabled={questionActionId === question.id}
              >
                Reject
              </button>
            )}
            {status !== 'proposed' && (
              <button
                className="btn"
                onClick={(e) => {
                  e.stopPropagation()
                  handleQuestionModeration(question.id, 'proposed')
                }}
                disabled={questionActionId === question.id}
              >
                Move to Proposed
              </button>
            )}
          </div>
        )}

        {kind === 'approved' && isStaff && status !== 'retired' && (
          <div className="row" style={{ gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
            <button
              className="btn"
              onClick={(e) => {
                e.stopPropagation()
                handleQuestionModeration(question.id, 'retired')
              }}
              disabled={questionActionId === question.id}
            >
              {questionActionId === question.id ? 'Updating...' : 'Retire'}
            </button>
            <button
              className="btn"
              onClick={(e) => {
                e.stopPropagation()
                handleQuestionModeration(question.id, 'rejected')
              }}
              disabled={questionActionId === question.id}
            >
              Reject
            </button>
          </div>
        )}

        {isExpanded && (
          runDetailLoading
            ? <div className="muted" style={{ fontSize: 12, padding: '10px 0' }}>Loading reasoning...</div>
            : runDetail && <RenderBoundary label="ForecastReasoning"><ForecastReasoningPanel detail={runDetail} /></RenderBoundary>
        )}
      </div>
    )
  }

  return (
    <RenderBoundary label="CompanyDetail">
    <div className="container">
      <Topbar
        subtitle={company.company_name || 'Company'}
        right={
          <div className="row" style={{ gap: 8 }}>
            {isTracked ? (
              <>
                <button className="btn primary" onClick={handleRefresh} disabled={refreshing}>
                  {refreshing ? 'Refreshing...' : 'Refresh'}
                </button>
                <button className="btn" onClick={handleUntrack}>Untrack</button>
              </>
            ) : (
              <button className="btn primary" onClick={handleTrack} disabled={refreshing}>
                {refreshing ? 'Tracking...' : 'Track'}
              </button>
            )}
          </div>
        }
      />
      <BackLink defaultTo="/universe/companies" defaultLabel="Companies" />

      {error && <ErrorBox error={error} />}
      {questionActionError && <ErrorBox error={new Error(questionActionError)} />}

      {/* Header card */}
      <div className="card" style={{ marginTop: 16 }}>
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <div>
            <h2 style={{ margin: 0 }}>{company.ticker} - {company.company_name}</h2>
            <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
              {company.sector && <span>{company.sector}</span>}
              {company.industry && <span> &middot; {company.industry}</span>}
            </div>
          </div>
          <div className="row" style={{ gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end', alignItems: 'center' }}>
            {isTracked ? <span className="pill ok">Tracked</span> : <span className="pill">Visible via pack/group</span>}
            {companyIdValid && (
              <DownloadReportButton kind="company" targetId={companyIdNum} />
            )}
          </div>
        </div>

        {/* Header pills focus on signals NOT covered by the memo metrics grid
            below (research freshness, monitors, staff-only overdue alerts).
            Approved / forecasted / watchpoint counts live in MemoMetric tiles
            inside the memo hero to avoid showing the same numbers twice. */}
        <div className="row" style={{ gap: 6, flexWrap: 'wrap', marginTop: 12 }}>
          {isStaff && proposedCount > 0 && (
            <span className="pill">{proposedCount} proposed</span>
          )}
          {companyHealth.last_successful_research_at && (
            <span className="pill">Research {timeAgo(companyHealth.last_successful_research_at)}</span>
          )}
          {companyHealth.last_monitor_hit_at && (
            <span className="pill">Monitor hit {timeAgo(companyHealth.last_monitor_hit_at)}</span>
          )}
          {isStaff && typeof companyHealth.overdue_monitor_count === 'number' && companyHealth.overdue_monitor_count > 0 && (
            <span className="pill bad">{companyHealth.overdue_monitor_count} overdue monitor{companyHealth.overdue_monitor_count === 1 ? '' : 's'}</span>
          )}
        </div>

        {/* Reverse links: the workspaces that cite this company. The company
            dossier is a satellite — always show the way back to where the
            user actually lives. */}
        {Array.isArray((data as any)?.workspace_refs) && (data as any).workspace_refs.length > 0 ? (
          <div className="row" style={{ gap: 6, flexWrap: 'wrap', marginTop: 12, alignItems: 'center' }}>
            <span className="muted" style={{ fontSize: 12 }}>In your workspaces:</span>
            {(data as any).workspace_refs.map((ref: any) => (
              <Link key={ref.workspace_id} className="pill" to={`/workspaces/${ref.workspace_id}`} style={{ textDecoration: 'none' }}>
                {String(ref.title || 'Workspace').slice(0, 60)}
              </Link>
            ))}
          </div>
        ) : null}
      </div>

      <div
        className="row"
        style={{ gap: 8, marginTop: 16, flexWrap: 'wrap' }}
        role="tablist"
        aria-label="Company view mode"
      >
        <button
          className={`btn ${viewMode === 'memo' ? 'primary' : ''}`}
          type="button"
          onClick={() => setViewMode('memo')}
          role="tab"
          aria-pressed={viewMode === 'memo'}
          aria-selected={viewMode === 'memo'}
        >
          Memo View
        </button>
        <button
          className={`btn ${viewMode === 'details' ? 'primary' : ''}`}
          type="button"
          onClick={() => setViewMode('details')}
          role="tab"
          aria-pressed={viewMode === 'details'}
          aria-selected={viewMode === 'details'}
        >
          Evidence & Questions
        </button>
      </div>

      {viewMode === 'memo' && (
        <CompanyMemoView
          currentView={currentView}
          whatChanged={whatChanged}
          whyItMatters={whyItMatters}
          watchpoints={watchpoints}
          questions={rankedApprovedQuestions}
          approvedCount={approvedCount}
          forecastedApprovedCount={forecastedApprovedCount}
          signals={signals}
          forecastReview={forecastReview}
          monitorUpdate={monitorUpdate}
          memoArtifact={memoArtifact}
          memoGeneratedAt={memoGeneratedAt}
          lastMeaningfulUpdateAt={lastMeaningfulUpdateAt}
          scenarioCardsPublicReady={scenarioCardsPublicReady}
          scenarioReadinessNote={scenarioReadinessNote}
        />
      )}

      {viewMode === 'details' && (
      <>
      {/* Executive summary / forecast synthesis near the top */}
      {report?.executive_summary && (
        <div className="card" style={{ marginTop: 16 }}>
          <h2 style={{ margin: '0 0 8px' }}>Executive Summary</h2>
          <div style={{ fontSize: 13, lineHeight: 1.6 }}>{report.executive_summary}</div>
          {report.generated_at && (
            <div className="muted" style={{ fontSize: 11, marginTop: 8 }}>
              Generated {timeAgo(report.generated_at)}
            </div>
          )}
        </div>
      )}

      {forecastReview?.executive_summary && (
        <div className="card" style={{ marginTop: 16, borderLeft: '3px solid var(--accent, #6366f1)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <h2 style={{ margin: 0 }}>{(forecastReview as any)?.is_live_snapshot ? 'Current Forecast Snapshot' : 'Forecast Review'}</h2>
            {(forecastReview as any)?.is_live_snapshot && (
              <span className="pill" title="No synthesized portfolio review yet; this is based on current forecasts.">
                current forecasts
              </span>
            )}
            {(forecastReview as any)?.is_stale && (
              <span className="pill" title="Newer individual forecasts exist; portfolio review synthesis is pending.">
                refresh pending
              </span>
            )}
          </div>
          <div style={{ fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{forecastReview.executive_summary}</div>
          {Array.isArray((forecastReview as any)?.evidence_library) && (forecastReview as any).evidence_library.length > 0 && !(forecastReview as any)?.is_live_snapshot && (
            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', opacity: 0.6, marginBottom: 4 }}>Key findings</div>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, lineHeight: 1.5 }}>
                {(forecastReview as any).evidence_library.slice(0, 5).map((f: string, i: number) => (
                  <li key={i}>{String(f)}</li>
                ))}
              </ul>
            </div>
          )}
          {forecastReview.generated_at && (
            <div className="muted" style={{ fontSize: 11, marginTop: 8 }}>
              Updated {timeAgo(forecastReview.generated_at)}
            </div>
          )}
        </div>
      )}

      {/* Scenarios near the top */}
      {scenarioCardsPublicReady && scenarioFrameCards.length > 0 ? (
        <div className="scenario-cards" style={{ marginTop: 16 }}>
          {scenarioFrameCards.map((frame: any, idx: number) => (
            <ScenarioFrameCard key={`${frame.caseKey || frame.label || 'scenario'}-${idx}`} frame={frame} compact />
          ))}
        </div>
      ) : null}

      <div className="twoCol" style={{ marginTop: 16 }}>
        <div className="card">
          <h2 style={{ margin: '0 0 8px' }}>Current View</h2>
          {currentView ? (
            <div style={{ fontSize: 13, lineHeight: 1.6 }}>{currentView}</div>
          ) : (
            <div className="muted" style={{ fontSize: 13 }}>No current view available yet.</div>
          )}
        </div>

        <div className="card">
          <h2 style={{ margin: '0 0 8px' }}>What Changed</h2>
          {whatChanged ? (
            <div style={{ fontSize: 13, lineHeight: 1.6 }}>{whatChanged}</div>
          ) : (
            <div className="muted" style={{ fontSize: 13 }}>No meaningful change captured yet.</div>
          )}
          {recentChangeItems.length > 0 && (
            <ul style={{ margin: '10px 0 0 18px', padding: 0, fontSize: 12, lineHeight: 1.5 }}>
              {recentChangeItems.map((item, idx) => (
                <li key={idx} style={{ marginBottom: 4 }}>{formatListItem(item)}</li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="twoCol" style={{ marginTop: 16 }}>
        <div className="card">
          <h2 style={{ margin: '0 0 8px' }}>Why It Matters</h2>
          {whyItMatters ? (
            <div style={{ fontSize: 13, lineHeight: 1.6 }}>{whyItMatters}</div>
          ) : (
            <div className="muted" style={{ fontSize: 13 }}>No explanation available yet.</div>
          )}
        </div>

        <div className="card">
          <h2 style={{ margin: '0 0 8px' }}>Watchpoints</h2>
          {watchpoints.length > 0 ? (
            <WatchpointList items={watchpoints.slice(0, 8)} />
          ) : (
            <div className="muted" style={{ fontSize: 13 }}>No watchpoints available yet.</div>
          )}
        </div>
      </div>

      <div className={isStaff ? 'twoCol' : 'grid'} style={{ marginTop: 16 }}>
        <div className="card">
          <h2 style={{ margin: '0 0 8px' }}>
            Key Questions ({rankedApprovedQuestions.length})
            {rankedApprovedQuestions.length > 0 && (
              <span className="muted" style={{ fontSize: 12, fontWeight: 400, marginLeft: 8 }}>
                {forecastedApprovedCount}/{rankedApprovedQuestions.length} current forecasted
              </span>
            )}
          </h2>
          <div className="stack">
            {visibleApprovedQuestions.map((question) => renderQuestionRow(question, 'approved'))}
            {!isStaff && hiddenApprovedCount > 0 && (
              <div className="row" style={{ justifyContent: 'space-between', marginTop: 8 }}>
                <div className="muted" style={{ fontSize: 12 }}>
                  Showing the highest-priority questions first.
                </div>
                <button className="btn" type="button" onClick={() => setShowAllQuestions((v) => !v)}>
                  {showAllQuestions ? 'Show fewer' : `Show ${hiddenApprovedCount} more`}
                </button>
              </div>
            )}
            {rankedApprovedQuestions.length === 0 && (
              <div className="muted" style={{ fontSize: 13 }}>
                {isStaff ? 'No approved questions yet.' : 'No questions yet.'}
              </div>
            )}
          </div>
        </div>

        {isStaff && (
          <div className="card">
            <h2 style={{ margin: '0 0 8px' }}>Proposed Questions ({proposedQuestions.length})</h2>
            <div className="stack">
              {proposedQuestions.slice(0, 15).map((question) => renderQuestionRow(question, 'proposed'))}
              {proposedQuestions.length === 0 && (
                <div className="muted" style={{ fontSize: 13 }}>
                  No proposed questions waiting for review.
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <CollapsibleCard
        title={`Latest Monitor Update`}
        hint="This is the most recent incremental update generated from monitors that watch news, filings, product launches, hiring, and other tracked developments for this company."
        summary={monitorUpdate?.executive_summary ? String(monitorUpdate.executive_summary).slice(0, 180) : 'Most recent monitor-driven delta update for this company.'}
      >
        {monitorUpdate?.executive_summary ? (
          <>
            <div style={{ fontSize: 13, lineHeight: 1.6 }}>{monitorUpdate.executive_summary}</div>
            {monitorUpdate.generated_at && (
              <div className="muted" style={{ fontSize: 11, marginTop: 8 }}>
                Updated {timeAgo(monitorUpdate.generated_at)}
              </div>
            )}
          </>
        ) : (
          <div className="muted" style={{ fontSize: 13 }}>No monitor update available yet.</div>
        )}
      </CollapsibleCard>

      {(hiringSignals.length > 0 || hiringRollup) && (
        <CollapsibleCard
          title={`What they're hiring for${hiringSignals.length ? ` (${hiringSignals.length})` : ''}`}
          hint="Derived from current job postings and careers-page evidence. Hiring is treated as forward-looking signal about where the company is directing build effort next."
          summary={(() => {
            const areas = Array.isArray(hiringRollup?.top_focus_areas) ? hiringRollup.top_focus_areas.slice(0, 3).join(', ') : ''
            if (areas) return `Focus areas: ${areas}.`
            if (hiringSignals.length > 0) return `${hiringSignals.length} hiring cluster${hiringSignals.length === 1 ? '' : 's'} extracted from careers / ATS evidence.`
            return 'Hiring rollup available.'
          })()}
        >
          {hiringRollup && (
            <div style={{ marginBottom: 12, padding: 10, borderRadius: 6, background: 'rgba(99,102,241,0.06)', borderLeft: '3px solid var(--accent, #6366f1)' }}>
              {Array.isArray(hiringRollup.top_focus_areas) && hiringRollup.top_focus_areas.length > 0 && (
                <div style={{ fontSize: 12, marginBottom: 6 }}>
                  <span style={{ fontWeight: 700, textTransform: 'uppercase', opacity: 0.7, fontSize: 10, marginRight: 6 }}>Top focus</span>
                  {hiringRollup.top_focus_areas.slice(0, 6).map((a: string, i: number) => (
                    <span key={i} className="pill" style={{ marginRight: 4 }}>{String(a)}</span>
                  ))}
                </div>
              )}
              {Array.isArray(hiringRollup.notable_clusters) && hiringRollup.notable_clusters.length > 0 && (
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, lineHeight: 1.5 }}>
                  {hiringRollup.notable_clusters.slice(0, 5).map((c: any, i: number) => (
                    <li key={i}>
                      <span style={{ fontWeight: 600 }}>{String(c?.label || 'cluster')}</span>
                      {typeof c?.count === 'number' && <span className="muted" style={{ marginLeft: 6 }}>×{c.count}</span>}
                      {c?.why_it_matters && <span className="muted"> — {String(c.why_it_matters)}</span>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          <div className="stack">
            {hiringSignals.slice(0, 20).map((sig: any) => {
              const d = sig.details_json || {}
              const skills = Array.isArray(d.skills) ? d.skills : []
              return (
                <div key={sig.id} className="signal-row">
                  <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                    <span className="signal-type">{d.role || sig.title}</span>
                    {d.seniority && <span className="pill">{String(d.seniority)}</span>}
                    {d.location && <span className="pill">{String(d.location)}</span>}
                    {d.team && <span className="pill">{String(d.team)}</span>}
                  </div>
                  {d.focus_area && (
                    <div style={{ fontSize: 12, marginTop: 4 }}>
                      <span style={{ fontWeight: 600 }}>Focus: </span>{String(d.focus_area)}
                    </div>
                  )}
                  {sig.summary && (
                    <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                      {String(sig.summary).slice(0, 240)}
                    </div>
                  )}
                  {skills.length > 0 && (
                    <div style={{ marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {skills.slice(0, 8).map((s: string, i: number) => (
                        <span key={i} className="pill" style={{ fontSize: 10 }}>{String(s)}</span>
                      ))}
                    </div>
                  )}
                  <div className="row" style={{ gap: 10, marginTop: 4, alignItems: 'center' }}>
                    {sig.source_url && (
                      <a href={sig.source_url} target="_blank" rel="noreferrer" style={{ fontSize: 11 }}>source</a>
                    )}
                    {sig.created_at && (
                      <span className="muted" style={{ fontSize: 11 }}>{timeAgo(sig.created_at)}</span>
                    )}
                  </div>
                </div>
              )
            })}
            {hiringSignals.length === 0 && !hiringRollup && (
              <div className="muted" style={{ fontSize: 13 }}>No hiring signals extracted yet.</div>
            )}
          </div>
        </CollapsibleCard>
      )}

      <div className="twoCol">
        <CollapsibleCard
          title={`Signals (${signals.length})`}
          hint="Signals are structured pieces of evidence extracted from fresh sources, like filings, product announcements, hiring moves, partnerships, and regulatory actions."
          summary={signals.length > 0 ? `${signals.length} recent signal${signals.length === 1 ? '' : 's'} extracted from monitored sources.` : 'No recent signals captured.'}
        >
          <div className="stack">
            {signals.slice(0, 15).map((sig: any) => (
              <div key={sig.id} className="signal-row">
                <div className="row" style={{ gap: 8 }}>
                  <span className="signal-type">{sig.signal_type}</span>
                  {sig.sentiment && (
                    <span className={`pill ${sig.sentiment === 'positive' ? 'ok' : sig.sentiment === 'negative' ? 'bad' : ''}`}>
                      {sig.sentiment}
                    </span>
                  )}
                </div>
                <div style={{ fontWeight: 600, fontSize: 13, marginTop: 4 }}>{sig.title}</div>
                {sig.summary && (
                  <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                    {String(sig.summary).slice(0, 160)}
                  </div>
                )}
                {sig.created_at && (
                  <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
                    {timeAgo(sig.created_at)}
                  </div>
                )}
              </div>
            ))}
            {signals.length === 0 && <div className="muted" style={{ fontSize: 13 }}>No signals yet</div>}
          </div>
        </CollapsibleCard>

        <CollapsibleCard
          title={`Monitors (${monitors.length})`}
          hint="Monitors are saved search/watch rules that continually check for new developments relevant to the company and its key questions."
          summary={monitors.length > 0 ? `${monitors.length} active or historical monitor${monitors.length === 1 ? '' : 's'} tracking company developments.` : 'No monitors configured.'}
        >
          <div className="stack">
            {monitors.slice(0, 10).map((m: any) => (
              <div key={m.id} className="monitor-row">
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{m.name || m.query}</div>
                  <span className={`pill ${m.is_active ? 'ok' : ''}`}>
                    {m.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
                  {m.last_run_at && <span>Ran {timeAgo(m.last_run_at)}</span>}
                  {m.last_hit_at && <span> &middot; Hit {timeAgo(m.last_hit_at)}</span>}
                </div>
              </div>
            ))}
            {monitors.length === 0 && <div className="muted" style={{ fontSize: 13 }}>No monitors</div>}
          </div>
        </CollapsibleCard>
      </div>

      <div className="twoCol">
        <CollapsibleCard
          title={`Report History (${reportHistory.length})`}
          hint="Historical research, monitor updates, and forecast-review summaries for this company."
          summary={reportHistory.length > 0 ? `Recent company reports and updates are available.` : 'No historical reports yet.'}
        >
          <div className="stack">
            {reportHistory.slice(0, 10).map((r: any) => (
              <div key={r.id} className="signal-row">
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <span className={`pill ${r.report_type === 'forecast_review' ? 'ok' : r.report_type === 'monitor_update' ? '' : 'ok'}`}>
                    {REPORT_TYPE_LABELS[r.report_type] || r.report_type}
                  </span>
                  <div className="muted" style={{ fontSize: 11 }}>
                    {r.generated_at ? timeAgo(r.generated_at) : ''}
                  </div>
                </div>
                {r.executive_summary && (
                  <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                    {String(r.executive_summary).slice(0, 120)}...
                  </div>
                )}
              </div>
            ))}
            {reportHistory.length === 0 && <div className="muted" style={{ fontSize: 13 }}>No reports yet</div>}
          </div>
        </CollapsibleCard>

        {isStaff && (
          <div className="card">
            <h2 style={{ margin: '0 0 8px' }}>Question Archive ({inactiveQuestions.length})</h2>
            <div className="stack">
              {inactiveQuestions.slice(0, 12).map((question) => renderQuestionRow(question, 'inactive'))}
              {inactiveQuestions.length === 0 && <div className="muted" style={{ fontSize: 13 }}>No inactive questions.</div>}
            </div>
          </div>
        )}
      </div>
      </>
      )}
    </div>
    </RenderBoundary>
  )
}
